(function () {
  if (window.tearsheetInitialized) return;
  window.tearsheetInitialized = true;

  // jsPDF (~365KB) and QRCode.js (~20KB) are only ever needed once someone
  // actually requests a tearsheet, so they aren't loaded up front like the
  // rest of the page — they're fetched on demand, right when generation is
  // triggered, and cached (via a single shared promise) so a second click
  // doesn't re-fetch or re-run the injected <script> tags.
  var TEARSHEET_LIB_URLS = [
    'https://cdn.jsdelivr.net/npm/jspdf@2.5.2/dist/jspdf.umd.min.js',
    'https://cdn.jsdelivr.net/npm/qrcodejs@1.0.0/qrcode.min.js'
  ];
  var tearsheetLibsPromise = null;

  function loadScriptOnce(src) {
    return new Promise(function (resolve, reject) {
      var existing = document.querySelector('script[src="' + src + '"]');
      if (existing) {
        if (existing.dataset.loaded === 'true') { resolve(); return; }
        existing.addEventListener('load', function () { resolve(); });
        existing.addEventListener('error', function () { reject(new Error('Failed to load ' + src)); });
        return;
      }
      var script = document.createElement('script');
      script.src = src;
      script.onload = function () { script.dataset.loaded = 'true'; resolve(); };
      script.onerror = function () { reject(new Error('Failed to load ' + src)); };
      document.head.appendChild(script);
    });
  }

  function ensureTearsheetLibs() {
    if (!tearsheetLibsPromise) {
      tearsheetLibsPromise = Promise.all(TEARSHEET_LIB_URLS.map(loadScriptOnce)).catch(function (err) {
        tearsheetLibsPromise = null; // allow a retry on the next click if this failed
        throw err;
      });
    }
    return tearsheetLibsPromise;
  }

  var DESIGN_SERVICES_URL = 'https://luminaire.com/pages/design-services';
  var SHOP_HOME_URL = 'https://luminaire.com';
  var IMAGE_RADIUS = 3; // mm — matches the site's rounded "block"/card image corners.
  var PHONE_WORD_REGEX = /^\(?\+?\d[\d().\-]{6,}\d$/;
  // Characters outside jsPDF's standard-font WinAnsiEncoding (roughly
  // Windows-1252). Most "smart" punctuation is fine (en/em dash, curly
  // quotes, ellipsis are all in that encoding), but a few aren't --
  // notably the non-breaking hyphen U+2011, which shows up whenever a rich
  // text editor auto-hyphenates a compound word like "forward-looking".
  // Hitting one of these mid-line throws off jsPDF's width/position math
  // for the rest of that line, which shows up as stretched, oddly
  // letter-spaced text. Normalize them to safe ASCII before any text
  // reaches jsPDF rather than relying on clean source data.
  var PDF_UNSAFE_CHAR_REGEX = new RegExp(
    '[\\u2010\\u2011\\u2012\\u2015\\u00AD\\u2060\\uFEFF\\u00A0\\u202F]',
    'g'
  );

  function sanitizeTextForPdf(str) {
    if (!str) return str;
    return str.replace(PDF_UNSAFE_CHAR_REGEX, function (ch) {
      var code = ch.charCodeAt(0);
      if (code === 0x00A0 || code === 0x202F) return ' '; // non-breaking / narrow no-break space
      if (code === 0x2060 || code === 0xFEFF) return '';  // word joiner / zero-width no-break space
      return '-'; // hyphen, non-breaking hyphen, figure dash, horizontal bar, soft hyphen
    });
  }

  function phoneToTelUri(word) {
    var digits = word.replace(/[^\d+]/g, '');
    if (digits.replace(/\D/g, '').length < 7) return null;
    if (digits.charAt(0) !== '+' && digits.length === 10) {
      digits = '+1' + digits; // assume a US number when no country code is given
    }
    return 'tel:' + digits;
  }

  // jsPDF's built-in doc.text(..., { align: 'justify' }) has shown a real
  // bug on some lines: it distributes the extra space between individual
  // characters instead of only between words, producing a stretched-out,
  // letter-spaced look. Doing the justify math ourselves and drawing each
  // word at its computed x position sidesteps that entirely.
  function drawJustifiedLine(doc, words, x, y, width) {
    if (words.length <= 1) {
      doc.text(words.join(' '), x, y);
      return;
    }
    var naturalWidth = 0;
    words.forEach(function (word) { naturalWidth += doc.getTextWidth(word); });
    var gap = Math.max((width - naturalWidth) / (words.length - 1), doc.getTextWidth(' '));
    var cursorX = x;
    words.forEach(function (word) {
      doc.text(word, cursorX, y);
      cursorX += doc.getTextWidth(word) + gap;
    });
  }

  function addRoundedImage(doc, imgData, format, x, y, w, h, radius) {
    var r = Math.min(radius == null ? IMAGE_RADIUS : radius, w / 2, h / 2);
    doc.saveGraphicsState();
    doc.roundedRect(x, y, w, h, r, r, null);
    doc.clip();
    doc.discardPath();
    doc.addImage(imgData, format, x, y, w, h);
    doc.restoreGraphicsState();
  }

  function imageIdentity(url) {
    if (!url) return '';
    var noQuery = url.split('?')[0];
    // Shopify CDN size suffixes (e.g. "_1200x1200", "_800x") differ between
    // a raw variant.featured_image.src and an image_url-filtered URL for the
    // exact same underlying photo — strip them so both compare as identical.
    return noQuery.replace(/_\d*x\d*(?=\.[a-zA-Z0-9]+$)/, '');
  }

  function toDataURL(url) {
    return fetch(url)
      .then(function (response) {
        if (!response.ok) throw new Error('Failed to fetch image: ' + url);
        return response.blob();
      })
      .then(function (blob) {
        return new Promise(function (resolve, reject) {
          var reader = new FileReader();
          reader.onloadend = function () { resolve(reader.result); };
          reader.onerror = reject;
          reader.readAsDataURL(blob);
        });
      });
  }

  function parseDescriptionBlocks(html) {
    var container = document.createElement('div');
    container.innerHTML = html || '';

    var blocks = [];

    function cleanText(node) {
      return sanitizeTextForPdf(node.textContent).replace(/\s+/g, ' ').trim();
    }

    function hasBoldWrapper(el) {
      if (!el.children || el.children.length !== 1) return false;
      var child = el.children[0];
      var tag = child.tagName.toLowerCase();
      if (tag !== 'strong' && tag !== 'b') return false;
      return cleanText(child) === cleanText(el);
    }

    function visit(node) {
      if (node.nodeType === 3) {
        var text = sanitizeTextForPdf(node.textContent).replace(/\s+/g, ' ').trim();
        if (text) blocks.push({ type: 'paragraph', text: text, bold: false });
        return;
      }
      if (node.nodeType !== 1) return;

      var tag = node.tagName.toLowerCase();

      if (tag === 'ul' || tag === 'ol') {
        Array.prototype.forEach.call(node.children, function (li) {
          var itemText = cleanText(li);
          if (itemText) blocks.push({ type: 'list', text: itemText, bold: false });
        });
        return;
      }

      if (tag === 'p' || /^h[1-6]$/.test(tag)) {
        var pText = cleanText(node);
        if (pText) blocks.push({ type: 'paragraph', text: pText, bold: hasBoldWrapper(node) });
        return;
      }

      if (tag === 'br') return;

      // Unknown wrapper (div, span, etc. at block level) — recurse into children.
      Array.prototype.forEach.call(node.childNodes, visit);
    }

    Array.prototype.forEach.call(container.childNodes, visit);

    if (!blocks.length) {
      var fallback = cleanText(container);
      if (fallback) blocks.push({ type: 'paragraph', text: fallback, bold: false });
    }

    return blocks;
  }

  // Parses rich-text HTML into paragraphs of word tokens, each optionally
  // carrying a link href (from an <a> ancestor) and a bold flag (from a
  // <strong>/<b> ancestor) — used for short inline runs like the tearsheet
  // footer text, where a user-authored link needs to stay clickable at
  // exactly the words it wraps rather than being flattened to plain text.
  function parseInlineWords(html) {
    var container = document.createElement('div');
    container.innerHTML = html || '';

    var paragraphs = [];
    var current = [];

    function pushWords(text, href, bold) {
      sanitizeTextForPdf(text).split(/\s+/).filter(Boolean).forEach(function (word) {
        current.push({ text: word, href: href || null, bold: !!bold });
      });
    }

    function endParagraph() {
      if (current.length) {
        paragraphs.push(current);
        current = [];
      }
    }

    function visit(node, href, bold) {
      if (node.nodeType === 3) {
        pushWords(node.textContent, href, bold);
        return;
      }
      if (node.nodeType !== 1) return;

      var tag = node.tagName.toLowerCase();
      if (tag === 'a') href = node.getAttribute('href') || href;
      if (tag === 'strong' || tag === 'b') bold = true;

      if (tag === 'br') {
        endParagraph();
        return;
      }

      Array.prototype.forEach.call(node.childNodes, function (child) {
        visit(child, href, bold);
      });

      if (tag === 'p' || /^h[1-6]$/.test(tag) || tag === 'div' || tag === 'li') {
        endParagraph();
      }
    }

    Array.prototype.forEach.call(container.childNodes, function (child) {
      visit(child, null, false);
    });
    endParagraph();

    return paragraphs;
  }

  function renderDescriptionBlocks(doc, blocks, x, startY, width, contentBottom, pageMargin) {
    var y = startY;
    var lineHeight = 4.6;
    var paragraphGap = 2.5;

    blocks.forEach(function (block, index) {
      if (index > 0) y += paragraphGap;

      doc.setFont('helvetica', block.bold ? 'bold' : 'normal').setFontSize(9.5);

      var prefix = block.type === 'list' ? '•  ' : '';
      var lines = doc.splitTextToSize(prefix + block.text, width);

      lines.forEach(function (line, lineIndex) {
        if (y > contentBottom) {
          doc.addPage();
          y = pageMargin;
        }
        // Justify every line except the last of a block — a stretched-out
        // final line looks wrong typographically, so it stays left-aligned.
        if (lineIndex === lines.length - 1) {
          doc.text(line, x, y);
        } else {
          drawJustifiedLine(doc, line.split(' '), x, y, width);
        }
        y += lineHeight;
      });
    });

    return y;
  }

  function renderInlineList(doc, items, x, startY, width, contentBottom, pageMargin) {
    var y = startY;
    if (y > contentBottom) {
      doc.addPage();
      y = pageMargin;
    }

    var lineHeight = 5;
    var separator = '   •   ';

    doc.setFont('helvetica', 'normal').setFontSize(9.5).setTextColor(0);
    var sepWidth = doc.getTextWidth(separator);

    var cursorX = x;
    items.forEach(function (item, index) {
      var text = item.text || item;
      var itemWidth = doc.getTextWidth(text);
      if (cursorX !== x && cursorX + itemWidth > x + width) {
        cursorX = x;
        y += lineHeight;
        if (y > contentBottom) {
          doc.addPage();
          y = pageMargin;
        }
      }
      doc.text(text, cursorX, y);
      if (item.url) {
        doc.link(cursorX, y - 3.2, itemWidth, 4, { url: item.url });
        doc.setDrawColor(0).line(cursorX, y + 0.8, cursorX + itemWidth, y + 0.8);
      }
      cursorX += itemWidth;
      if (index < items.length - 1) {
        doc.text(separator, cursorX, y);
        cursorX += sepWidth;
      }
    });

    return y + lineHeight;
  }

  function renderBrandMark(doc, vendor, logoDataUrl, linkUrl, x, startY, width, contentBottom, pageMargin) {
    var y = startY;
    if (y > contentBottom) {
      doc.addPage();
      y = pageMargin;
    }

    var centerX = x + width / 2;

    if (logoDataUrl) {
      try {
        var logoProps = doc.getImageProperties(logoDataUrl);
        var logoHeight = 20;
        var logoWidth = logoHeight * (logoProps.width / logoProps.height);
        if (y + logoHeight > contentBottom) {
          doc.addPage();
          y = pageMargin;
        }
        doc.addImage(logoDataUrl, 'PNG', centerX - logoWidth / 2, y, logoWidth, logoHeight);
        if (linkUrl) doc.link(centerX - logoWidth / 2, y, logoWidth, logoHeight, { url: linkUrl });
        return y + logoHeight + 6;
      } catch (e) { /* fall through to name text below */ }
    }

    doc.setFont('helvetica', 'normal').setFontSize(11);
    doc.text(vendor, centerX, y + 4, { align: 'center' });
    if (linkUrl) {
      var nameWidth = doc.getTextWidth(vendor);
      doc.link(centerX - nameWidth / 2, y - 2, nameWidth, 8, { url: linkUrl });
    }
    return y + 10;
  }

  function renderBioWrappedAroundImage(doc, blocks, x, width, photoRight, photoBottom, startY, contentBottom, pageMargin, padding) {
    var y = startY;
    var lineHeight = 4.6;
    var paragraphGap = 2.5;
    var pastPhoto = y > photoBottom + padding;

    blocks.forEach(function (block, index) {
      var pendingGap = index > 0 ? paragraphGap : 0;

      doc.setFont('helvetica', block.bold ? 'bold' : 'normal').setFontSize(9.5);

      var prefix = block.type === 'list' ? '•  ' : '';
      var words = (prefix + block.text).split(/\s+/).filter(Boolean);
      var i = 0;

      while (i < words.length) {
        if (y + pendingGap > contentBottom) {
          doc.addPage();
          y = pageMargin;
          pastPhoto = true;
          pendingGap = 0;
        }
        // doc.text() draws at the text BASELINE, so a line's glyphs actually
        // start rendering above y by roughly the font's ascent. Rather than
        // injecting an artificial gap the moment text clears the photo
        // (which reads as a stray hole when it lands mid-sentence), keep
        // drawing lines in the narrow "beside photo" column a little longer
        // — until they've naturally accumulated the same clearance below
        // the photo as the padding used beside it. No extra whitespace is
        // ever inserted; the buffer comes purely from normal line-height
        // steps, so the transition still reads as continuous text.
        if (!pastPhoto && (y + pendingGap - lineHeight * 0.75) > photoBottom + padding) {
          pastPhoto = true;
        }

        y += pendingGap;
        pendingGap = 0;

        var lineX = pastPhoto ? x : photoRight;
        var lineWidth = pastPhoto ? width : width - (photoRight - x);

        var testLine = '';
        var lineWords = [];
        while (i < words.length) {
          var candidate = testLine ? testLine + ' ' + words[i] : words[i];
          if (testLine && doc.getTextWidth(candidate) > lineWidth) break;
          testLine = candidate;
          lineWords.push(words[i]);
          i++;
        }

        var isLastLineOfBlock = i >= words.length;
        if (!isLastLineOfBlock && lineWords.length > 1) {
          drawJustifiedLine(doc, lineWords, lineX, y, lineWidth);
        } else {
          doc.text(testLine, lineX, y);
        }
        y += lineHeight;
      }
    });

    return y;
  }

  function renderDesignerSection(doc, label, names, photoDataUrl, bio, linkUrl, x, startY, width, contentBottom, pageMargin) {
    var y = startY;
    if (y > contentBottom) {
      doc.addPage();
      y = pageMargin;
    }

    var photoColWidth = 45;
    var padding = 8;
    var textX = x;
    var textWidth = width;
    var photoBottom = y;

    if (photoDataUrl) {
      try {
        var props = doc.getImageProperties(photoDataUrl);
        var photoW = photoColWidth;
        var photoH = photoW * (props.height / props.width);
        var maxPhotoH = 60;
        if (photoH > maxPhotoH) {
          photoH = maxPhotoH;
          photoW = photoH * (props.width / props.height);
        }
        if (y + photoH > contentBottom) {
          doc.addPage();
          y = pageMargin;
        }
        addRoundedImage(doc, photoDataUrl, 'JPEG', x, y, photoW, photoH);
        if (linkUrl) doc.link(x, y, photoW, photoH, { url: linkUrl });
        photoBottom = y + photoH;
        textX = x + photoColWidth + padding;
        textWidth = width - photoColWidth - padding;
      } catch (e) {
        console.error('[Tearsheet] designer photo failed to embed in PDF:', e);
      }
    } else {
      console.warn('[Tearsheet] no designer photo data URL was available to render (fetch may have failed or the attribute was empty).');
    }

    // doc.text() draws at the text baseline; nudge down by roughly the label's
    // cap-height so the top of "DESIGNER" visually lines up with the photo's
    // top edge instead of sitting noticeably below it.
    var textY = y + 2.3;
    doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(211, 26, 45);
    doc.text(label.toUpperCase(), textX, textY);
    doc.setTextColor(0);
    textY += 5;

    doc.setFont('helvetica', 'bold').setFontSize(11);
    var nameLines = doc.splitTextToSize(names, textWidth);
    nameLines.forEach(function (line) {
      doc.text(line, textX, textY);
      if (linkUrl) {
        var lineWidth = doc.getTextWidth(line);
        doc.link(textX, textY - 4, lineWidth, 5, { url: linkUrl });
      }
      textY += 5;
    });

    if (bio) {
      textY += 3;
      var bioBlocks = parseDescriptionBlocks(bio);
      textY = renderBioWrappedAroundImage(doc, bioBlocks, x, width, textX, photoBottom, textY, contentBottom, pageMargin, padding);
    }

    return Math.max(textY, photoBottom) + 4;
  }

  function renderDesignServicesPromo(doc, x, startY, width, footerText) {
    var headlineFontSize = 16;
    var headlineLineHeight = 8.4;
    var lineHeight = 4.2;
    var smallLineHeight = 3.6;
    var gap = 2;
    var smallGap = 3;

    var subtextWords = [
      { text: 'Schedule', underline: false },
      { text: 'your', underline: false },
      { text: 'Exclusive', underline: true },
      { text: 'Complimentary', underline: true },
      { text: 'Consultation', underline: true },
      { text: 'for', underline: false },
      { text: 'a', underline: false },
      { text: 'bespoke', underline: false },
      { text: 'journey.', underline: false }
    ];

    doc.setFont('helvetica', 'bold').setFontSize(headlineFontSize);
    var headlineLines = doc.splitTextToSize('Discover The Total Design Experience.', width);

    doc.setFont('helvetica', 'normal').setFontSize(8);
    var spaceWidth = doc.getTextWidth(' ');

    var y = startY + headlineLineHeight * 0.75;

    doc.setFont('helvetica', 'bold').setFontSize(headlineFontSize).setTextColor(211, 26, 45);
    headlineLines.forEach(function (line) {
      var lineWidth = doc.getTextWidth(line);
      doc.text(line, x, y);
      doc.link(x, y - 6, lineWidth, 8, { url: DESIGN_SERVICES_URL });
      y += headlineLineHeight;
    });

    y += gap;

    doc.setFont('helvetica', 'normal').setFontSize(8).setTextColor(120);
    var cursorX = x;
    var underlineStartX = null;
    var underlineEndX = null;

    function flushUnderline() {
      if (underlineStartX === null) return;
      doc.setDrawColor(120).line(underlineStartX, y + 0.8, underlineEndX, y + 0.8);
      doc.link(underlineStartX, y - 3, underlineEndX - underlineStartX, 4, { url: DESIGN_SERVICES_URL });
      underlineStartX = null;
      underlineEndX = null;
    }

    subtextWords.forEach(function (word) {
      var wordWidth = doc.getTextWidth(word.text);
      if (cursorX !== x && cursorX + wordWidth > x + width) {
        flushUnderline();
        cursorX = x;
        y += lineHeight;
      }
      doc.text(word.text, cursorX, y);
      if (word.underline) {
        if (underlineStartX === null) underlineStartX = cursorX;
        underlineEndX = cursorX + wordWidth;
      } else {
        flushUnderline();
      }
      cursorX += wordWidth + spaceWidth;
    });
    flushUnderline();
    y += lineHeight;

    y += smallGap;
    if (footerText) {
      var footerParagraphs = parseInlineWords(footerText);
      footerParagraphs.forEach(function (words, pIndex) {
        if (pIndex > 0) y += smallGap;

        var footerCursorX = x;
        var footerUnderlineStartX = null;
        var footerUnderlineEndX = null;

        function flushFooterUnderline() {
          if (footerUnderlineStartX === null) return;
          doc.setDrawColor(120).line(footerUnderlineStartX, y + 0.8, footerUnderlineEndX, y + 0.8);
          footerUnderlineStartX = null;
          footerUnderlineEndX = null;
        }

        words.forEach(function (word) {
          // Auto-link a bare phone number that isn't already an explicit
          // link, same as the rest of the footer's link styling.
          var href = word.href;
          if (!href && PHONE_WORD_REGEX.test(word.text)) href = phoneToTelUri(word.text);

          doc.setFont('helvetica', word.bold ? 'bold' : 'normal').setFontSize(7.5).setTextColor(120);
          var footerSpaceWidth = doc.getTextWidth(' ');
          var wordWidth = doc.getTextWidth(word.text);

          if (footerCursorX !== x && footerCursorX + wordWidth > x + width) {
            flushFooterUnderline();
            footerCursorX = x;
            y += smallLineHeight;
          }

          doc.text(word.text, footerCursorX, y);

          if (href) {
            doc.link(footerCursorX, y - 2.8, wordWidth, 3.4, { url: href });
            if (footerUnderlineStartX === null) footerUnderlineStartX = footerCursorX;
            footerUnderlineEndX = footerCursorX + wordWidth;
          } else {
            flushFooterUnderline();
          }

          footerCursorX += wordWidth + footerSpaceWidth;
        });
        flushFooterUnderline();
        y += smallLineHeight;
      });
    }

    doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(120);
    var dateStatement = 'This document was generated on ' + new Date().toLocaleDateString() + '.';
    doc.text(dateStatement, x, y);
    y += smallLineHeight;

    doc.setTextColor(0);
    return y;
  }

  function generateQrDataUrl(text) {
    if (!window.QRCode) return null;

    var holder = document.createElement('div');
    holder.style.cssText = 'position:absolute;left:-9999px;top:-9999px;';
    document.body.appendChild(holder);

    new window.QRCode(holder, {
      text: text,
      width: 256,
      height: 256,
      correctLevel: window.QRCode.CorrectLevel.M
    });

    var canvas = holder.querySelector('canvas');
    var dataUrl = canvas ? canvas.toDataURL('image/png') : null;
    document.body.removeChild(holder);
    return dataUrl;
  }

  function buildPdf(data) {
    var jsPDF = window.jspdf.jsPDF;
    var doc = new jsPDF({ unit: 'mm', format: 'letter' });

    // data.title is already "Vendor | Product Title" (plus a variant suffix
    // when applicable) — strip the vendor prefix back off to get the plain
    // product title for the Subject/Keywords fields below.
    var plainTitle = data.title || '';
    if (data.vendor && plainTitle.indexOf(data.vendor + ' | ') === 0) {
      plainTitle = plainTitle.slice((data.vendor + ' | ').length);
    }
    doc.setProperties({
      title: data.title || '',
      author: 'Luminaire',
      subject: [data.vendor, plainTitle, 'Tearsheet'].filter(Boolean).join(' '),
      keywords: [data.vendor, plainTitle].filter(Boolean).join(', ')
    });

    var pageWidth = doc.internal.pageSize.getWidth();
    var pageHeight = doc.internal.pageSize.getHeight();
    var margin = 15;
    var contentWidth = pageWidth - margin * 2;
    var y = margin;

    if (data.logoDataUrl) {
      try {
        var logoProps = doc.getImageProperties(data.logoDataUrl);
        var logoHeight = 7;
        var logoWidth = (logoProps.width / logoProps.height) * logoHeight;
        doc.addImage(data.logoDataUrl, 'PNG', margin, y, logoWidth, logoHeight);
        doc.link(margin, y, logoWidth, logoHeight, { url: SHOP_HOME_URL });
      } catch (e) { /* fall back to no logo */ }
    } else if (data.shopName) {
      doc.setFont('helvetica', 'bold').setFontSize(14);
      doc.text(data.shopName, margin, y + 8);
      var shopNameWidth = doc.getTextWidth(data.shopName);
      doc.link(margin, y, shopNameWidth, 8, { url: SHOP_HOME_URL });
    }

    y += 22;
    doc.setDrawColor(220).line(margin, y, pageWidth - margin, y);
    y += 10;

    doc.setFont('helvetica', 'bold').setFontSize(18).setTextColor(211, 26, 45);
    var titleLines = doc.splitTextToSize(data.title || '', contentWidth);
    doc.text(titleLines, margin, y);
    doc.setTextColor(0);
    y += titleLines.length * 7 + 6;

    // Single image row: as many images as fit at a common height, left to
    // right. Whatever doesn't fit is simply left out of the row.
    var rowHeight = 55;
    var imageGap = 4;
    var rowX = margin;
    var imagesDrawn = 0;
    var allImages = [data.mainImageDataUrl].concat(data.otherImageDataUrls).filter(Boolean);

    allImages.forEach(function (imgUrl) {
      try {
        var props = doc.getImageProperties(imgUrl);
        var imgW = rowHeight * (props.width / props.height);

        if (rowX + imgW > pageWidth - margin) return;

        addRoundedImage(doc, imgUrl, 'JPEG', rowX, y, imgW, rowHeight);
        if (data.productUrl) doc.link(rowX, y, imgW, rowHeight, { url: data.productUrl });
        rowX += imgW + imageGap;
        imagesDrawn += 1;
      } catch (e) { /* skip broken image */ }
    });

    var imageBottom = imagesDrawn > 0 ? y + rowHeight : y;

    // The divider/QR/promo/footer bar only ever gets drawn once, at the very
    // end, on whichever page body content happens to end on — so body flow
    // itself should use the FULL page height, not a bottom zone reserved for
    // that bar on every single page. Reserving it everywhere just forced
    // early page breaks and left large dead space at the bottom of every
    // non-final page.
    var qrSize = 26;
    var footerLineY = pageHeight - margin - qrSize - 10;
    var qrY = footerLineY + 6;
    var contentBottom = pageHeight - margin;

    var textY = imageBottom + 10;

    if (data.description) {
      var descriptionBlocks = parseDescriptionBlocks(data.description);
      textY = renderDescriptionBlocks(doc, descriptionBlocks, margin, textY, contentWidth, contentBottom, margin);
    }

    if (data.variantItems && data.variantItems.length) {
      textY += 6;
      if (textY > contentBottom) {
        doc.addPage();
        textY = margin;
      }
      doc.setFont('helvetica', 'bold').setFontSize(9).setTextColor(211, 26, 45);
      doc.text('AVAILABLE OPTIONS', margin, textY);
      doc.setTextColor(0);
      textY += 9;
      textY = renderInlineList(doc, data.variantItems, margin, textY, contentWidth, contentBottom, margin);
    }

    if (data.vendor) {
      textY += 6;
      textY = renderBrandMark(doc, data.vendor, data.brandLogoDataUrl, data.brandUrl, margin, textY, contentWidth, contentBottom, margin);

      if (data.brandInfo) {
        textY += 3;
        var brandInfoBlocks = parseDescriptionBlocks(data.brandInfo);
        textY = renderDescriptionBlocks(doc, brandInfoBlocks, margin, textY, contentWidth, contentBottom, margin);
      }
    }

    if (data.designer) {
      textY += 6;
      var designerLabel = data.designer.indexOf('\n') !== -1 ? 'Designers' : 'Designer';
      textY = renderDesignerSection(doc, designerLabel, data.designer, data.designerPhotoDataUrl, data.designerBio, data.designerUrl, margin, textY, contentWidth, contentBottom, margin);
    }

    // Now that body content is done, make sure the bottom bar actually has
    // room on the current page — only page-break here, once, if it doesn't.
    if (textY > footerLineY) {
      doc.addPage();
    }

    doc.setDrawColor(220).line(margin, footerLineY, pageWidth - margin, footerLineY);

    // QR sits bottom-right; the design-services promo fills the space
    // across from it on the left, with the footer text and generated-on
    // date stacked underneath it, left-aligned.
    var qrX = pageWidth - margin - qrSize;
    if (data.qrDataUrl) {
      doc.addImage(data.qrDataUrl, 'PNG', qrX, qrY, qrSize, qrSize);
      doc.setFont('helvetica', 'normal').setFontSize(7.5).setTextColor(120);
      doc.text('Scan to view online', qrX + qrSize / 2, qrY + qrSize + 4, { align: 'center' });
      doc.setTextColor(0);
    }

    var promoWidth = contentWidth - qrSize - 10;
    renderDesignServicesPromo(doc, margin, qrY, promoWidth, data.footer);

    var fileName = (data.title || 'product').replace(/[^a-z0-9]+/gi, '-').toLowerCase() + '-tearsheet.pdf';
    doc.save(fileName);
  }

  function generateTearsheet(trigger) {
    // The clicked element (button, link, accordion row — anything with
    // data-tearsheet-button) only supplies busy-state UI feedback. The
    // actual product data lives on a single shared element rendered once
    // per page by snippets/tearsheet-data.liquid, so any trigger anywhere
    // on the page works without having to carry the data itself.
    var data = document.querySelector('[data-tearsheet-data]');
    if (!data) {
      console.error('[Tearsheet] no [data-tearsheet-data] element found on the page — add the "Tearsheet Data" section to this template.');
      return;
    }

    if (trigger) {
      trigger.setAttribute('aria-busy', 'true');
      trigger.classList.add('pointer-events-none');
    }

    ensureTearsheetLibs()
      .then(function () { buildTearsheet(trigger, data); })
      .catch(function (err) {
        console.error('[Tearsheet] failed to load PDF library:', err);
        if (trigger) {
          trigger.removeAttribute('aria-busy');
          trigger.classList.remove('pointer-events-none');
        }
      });
  }

  function buildTearsheet(trigger, data) {
    if (!window.jspdf || !window.jspdf.jsPDF) {
      console.error('[Tearsheet] jsPDF failed to load.');
      if (trigger) {
        trigger.removeAttribute('aria-busy');
        trigger.classList.remove('pointer-events-none');
      }
      return;
    }

    var imagesScript = document.querySelector('[data-tearsheet-images]');
    var selectedVariantScript = document.querySelector('[data-selected-variant]');
    var variantsScript = document.querySelector('[data-variants]');

    var images = [];
    if (imagesScript) {
      try { images = JSON.parse(imagesScript.textContent) || []; } catch (e) { images = []; }
    }

    var variant = null;
    if (selectedVariantScript) {
      try { variant = JSON.parse(selectedVariantScript.textContent); } catch (e) { variant = null; }
    }

    var allVariants = [];
    if (variantsScript) {
      try { allVariants = JSON.parse(variantsScript.textContent) || []; } catch (e) { allVariants = []; }
    }

    var vendor = sanitizeTextForPdf(data.dataset.tearsheetVendor || '');
    var title = sanitizeTextForPdf(data.dataset.tearsheetTitle || '');
    if (variant && variant.title && variant.title !== 'Default Title') {
      title += ' - ' + sanitizeTextForPdf(variant.title);
    }
    if (vendor) {
      title = vendor + ' | ' + title;
    }

    var baseUrl = data.dataset.tearsheetUrl || '';

    var variantItems = allVariants.length > 1
      ? allVariants
          .map(function (v) {
            var label = v.title === 'Default Title' ? '' : sanitizeTextForPdf(v.title);
            var text = (label + (v.available ? '' : ' (Out of stock)')).trim();
            return text ? { text: text, url: baseUrl + '?variant=' + v.id } : null;
          })
          .filter(Boolean)
      : [];

    var logoUrl = data.dataset.tearsheetLogo || '';
    var brandLogoUrl = data.dataset.tearsheetBrandLogo || '';
    var brandUrl = data.dataset.tearsheetBrandUrl || '';
    var designerPhotoUrl = data.dataset.tearsheetDesignerPhoto || '';
    var designerUrl = data.dataset.tearsheetDesignerUrl || '';
    var variantImage = variant && variant.featured_image && variant.featured_image.src;
    var mainImage = variantImage || images[0] || null;
    var mainImageKey = imageIdentity(mainImage);
    var seenImageKeys = {};
    seenImageKeys[mainImageKey] = true;
    var otherImages = images.filter(function (src) {
      var key = imageIdentity(src);
      if (seenImageKeys[key]) return false;
      seenImageKeys[key] = true;
      return true;
    });
    var qrTargetUrl = baseUrl + (variant && variant.id ? '?variant=' + variant.id : '');

    var imagesToFetch = [mainImage].concat(otherImages).filter(Boolean);
    if (logoUrl) imagesToFetch.push(logoUrl);
    if (brandLogoUrl) imagesToFetch.push(brandLogoUrl);
    if (designerPhotoUrl) imagesToFetch.push(designerPhotoUrl);

    var designerBioValue = data.dataset.tearsheetDesignerBio || '';
    console.log(
      '[Tearsheet] designer diagnostics —',
      'photo attribute:', designerPhotoUrl || '(empty)',
      '| bio attribute length:', designerBioValue.length,
      '| bio preview:', designerBioValue ? designerBioValue.slice(0, 80) + '…' : '(empty)'
    );

    Promise.all(
      imagesToFetch.map(function (url) {
        return toDataURL(url)
          .catch(function (err) {
            if (url === designerPhotoUrl) {
              console.error('[Tearsheet] designer photo fetch failed for URL:', url, err);
            }
            return null;
          });
      })
    )
      .then(function (dataUrls) {
        var urlMap = {};
        imagesToFetch.forEach(function (url, i) { urlMap[url] = dataUrls[i]; });

        if (designerPhotoUrl && !urlMap[designerPhotoUrl]) {
          console.warn('[Tearsheet] designer photo URL was present but fetch produced no data:', designerPhotoUrl);
        }

        buildPdf({
          title: title,
          vendor: vendor,
          brandInfo: data.dataset.tearsheetBrandInfo || '',
          designer: sanitizeTextForPdf(data.dataset.tearsheetDesigner || ''),
          designerBio: designerBioValue,
          description: data.dataset.tearsheetDescription || '',
          shopName: sanitizeTextForPdf(data.dataset.tearsheetShopName || ''),
          footer: data.dataset.tearsheetFooter || '',
          productUrl: qrTargetUrl,
          brandUrl: brandUrl,
          designerUrl: designerUrl,
          logoDataUrl: logoUrl ? urlMap[logoUrl] : null,
          brandLogoDataUrl: brandLogoUrl ? urlMap[brandLogoUrl] : null,
          designerPhotoDataUrl: designerPhotoUrl ? urlMap[designerPhotoUrl] : null,
          mainImageDataUrl: mainImage ? urlMap[mainImage] : null,
          otherImageDataUrls: otherImages.map(function (url) { return urlMap[url]; }).filter(Boolean),
          variantItems: variantItems,
          qrDataUrl: generateQrDataUrl(qrTargetUrl)
        });
      })
      .catch(function (error) {
        console.error('[Tearsheet]', error);
      })
      .finally(function () {
        if (trigger) {
          trigger.removeAttribute('aria-busy');
          trigger.classList.remove('pointer-events-none');
        }
      });
  }

  document.addEventListener('click', function (event) {
    var button = event.target.closest('[data-tearsheet-button]');
    if (!button || button.hasAttribute('aria-busy')) return;

    event.preventDefault();
    generateTearsheet(button);
  });

  // Deep-link support: any link to a product page with ?download_tearsheet=1
  // appended (optionally alongside the theme's own ?variant= param, which
  // the variant picker already reads on load) generates and downloads that
  // product's tearsheet immediately — no click on the button required. This
  // lets a plain <a href="/products/handle?download_tearsheet=1"> work from
  // anywhere: a blog article, the product details accordion, etc.
  var deepLinkParams = new URLSearchParams(window.location.search);
  if (deepLinkParams.get('download_tearsheet') === '1') {
    // A visible trigger isn't required for the deep link to work — only the
    // shared [data-tearsheet-data] element is — but if one happens to be on
    // the page, pass it through so it gets the same busy-state UI feedback.
    generateTearsheet(document.querySelector('[data-tearsheet-button]'));
  }
})();
